import { router } from "@inertiajs/react";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import type { Category, Subcategory } from "@/types";

import { AddSubcategoryButton, SubcategoryForm } from "./subcategory-form";

interface SubcategoryListProps {
  category: Category;
  subcategories: Subcategory[];
}

export function SubcategoryList({
  category,
  subcategories,
}: SubcategoryListProps) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const handleDelete = (subcategory: Subcategory) => {
    if (
      confirm(
        `¿Estás seguro de que deseas eliminar la subcategoría "${subcategory.name}"? Esta acción no se puede deshacer.`,
      )
    ) {
      setDeletingId(subcategory.id);
      router.delete(
        `/categories/${category.id}/subcategories/${subcategory.id}`,
        {
          preserveScroll: true,
          onFinish: () => setDeletingId(null),
        },
      );
    }
  };

  const handleRefresh = () => {
    router.reload({ only: ["category"] });
  };

  if (subcategories.length === 0) {
    return (
      <div className="space-y-4">
        <div className="text-center py-8 text-muted-foreground">
          <p>No hay subcategorías en esta categoría</p>
          <p className="text-sm">
            Agrega la primera subcategoría usando el botón de abajo
          </p>
        </div>
        <AddSubcategoryButton category={category} onSuccess={handleRefresh} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {subcategories.map((subcategory) => (
          <div key={subcategory.id}>
            {editingId === subcategory.id ? (
              <SubcategoryForm
                category={category}
                subcategory={subcategory}
                onCancel={() => setEditingId(null)}
                onSuccess={() => {
                  setEditingId(null);
                  handleRefresh();
                }}
              />
            ) : (
              <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                <div className="flex-1">
                  <h4 className="font-medium">{subcategory.name}</h4>
                  {subcategory.description && (
                    <p className="text-sm text-muted-foreground">
                      {subcategory.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingId(subcategory.id)}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    disabled={deletingId === subcategory.id}
                    onClick={() => handleDelete(subcategory)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <AddSubcategoryButton category={category} onSuccess={handleRefresh} />
    </div>
  );
}
